import { useEffect } from "react";

/*
 * Helpers for dropdown menus that open from inside scrollable tables.
 * Tables scroll horizontally (overflow-x: auto), which would clip an absolutely
 * positioned menu, so the menu is positioned `fixed` next to the button instead.
 */

const MENU_WIDTH = 260;
const MENU_MAX_HEIGHT = 340;
const GAP = 6;
const EDGE = 8;

// Style for a menu anchored to `element` (usually event.currentTarget).
export const getAnchoredMenuStyle = (element) => {
  if (!element?.getBoundingClientRect) return null;

  const rect = element.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const left = Math.min(
    Math.max(EDGE, rect.right - MENU_WIDTH),
    viewportWidth - MENU_WIDTH - EDGE,
  );

  const spaceBelow = viewportHeight - rect.bottom - EDGE;
  const spaceAbove = rect.top - EDGE;
  const openUp = spaceBelow < Math.min(MENU_MAX_HEIGHT, 240) && spaceAbove > spaceBelow;

  return {
    position: "fixed",
    left,
    right: "auto",
    width: MENU_WIDTH,
    maxHeight: Math.max(160, Math.min(MENU_MAX_HEIGHT, (openUp ? spaceAbove : spaceBelow) - GAP)),
    overflowY: "auto",
    zIndex: 1040,
    ...(openUp
      ? { top: "auto", bottom: viewportHeight - rect.top + GAP }
      : { top: rect.bottom + GAP, bottom: "auto" }),
  };
};

// Close the menu when anything scrolls or the window resizes (its anchor moves).
export const useCloseOnScroll = (isOpen, close) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const handle = () => close();
    window.addEventListener("scroll", handle, true);
    window.addEventListener("resize", handle);
    return () => {
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle);
    };
  }, [isOpen, close]);
};
